import { InjectionToken, Optional, Inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { share } from 'rxjs/operators';

/**
 * Layout direction.
 * */
export enum ElLayoutDirection {
  LTR = 'ltr',
  RTL = 'rtl',
};

/**
 * Layout direction setting injection token.
 * */
export const EL_LAYOUT_DIRECTION = new InjectionToken<ElLayoutDirection>('Layout direction');

/**
 * Layout Direction Service.
 * Allows to set or get layout direction and listen to its changes
 */
@Injectable()
export class ElLayoutDirectionService {
  private $directionChange = new ReplaySubject(1);

  constructor(
    @Optional() @Inject(EL_LAYOUT_DIRECTION) private direction = ElLayoutDirection.LTR,
  ) {
    this.setDirection(direction);
  }

  /**
   * Returns true if layout direction set to left to right.
   * @returns boolean.
   * */
  public isLtr(): boolean {
    return this.direction === ElLayoutDirection.LTR;
  }

  /**
   * Returns true if layout direction set to right to left.
   * @returns boolean.
   * */
  public isRtl(): boolean {
    return this.direction === ElLayoutDirection.RTL;
  }

  /**
   * Returns current layout direction.
   * @returns ElLayoutDirection.
   * */
  getDirection(): ElLayoutDirection {
    return this.direction;
  }

  /**
   * Sets layout direction
   * @param {ElLayoutDirection} direction
   */
  setDirection(direction: ElLayoutDirection) {
    this.direction = direction;
    this.$directionChange.next(direction);
  }

  /**
   * Triggered when direction was changed.
   * @returns Observable<ElLayoutDirection>.
   */
  onDirectionChange(): Observable<ElLayoutDirection> {
    return this.$directionChange.pipe(share<ElLayoutDirection>());
  }
}
